// 环境类型定义
type Environment = 'development' | 'test' | 'production';

// 获取当前环境
const getCurrentEnvironment = (): Environment => {
  // 优先使用环境变量
  const envFromVar = process.env.REACT_APP_ENV;
  if (envFromVar && ['development', 'test', 'production'].includes(envFromVar)) {
    return envFromVar as Environment;
  }
  
  // 回退到NODE_ENV
  const env = process.env.NODE_ENV;
  if (env === 'production') {
    return 'production';
  } else if (env === 'test') {
    return 'test';
  }
  return 'development';
};

// 环境配置接口
interface EnvironmentConfig {
  // API配置
  apiBaseUrl: string;
  apiTimeout: number;
  
  // 应用配置
  appName: string;
  appVersion: string;
  
  // 功能开关
  features: {
    enableAnalytics: boolean;
    enableDebug: boolean;
    enableMockData: boolean;
  };
  
  // 第三方服务配置
  services: {
    uploadUrl: string;
    cdnUrl: string;
  };
  
  // 日志配置
  logging: {
    level: 'debug' | 'info' | 'warn' | 'error';
    enableConsole: boolean;
  };
}

// 开发环境配置
const developmentConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api',
  apiTimeout: 10000,
  appName: '充值管理系统 (开发版)',
  appVersion: '1.0.0-dev',
  features: {
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
    enableDebug: process.env.REACT_APP_ENABLE_DEBUG !== 'false',
    enableMockData: process.env.REACT_APP_ENABLE_MOCK_DATA !== 'false',
  },
  services: {
    uploadUrl: process.env.REACT_APP_UPLOAD_URL || 'http://localhost:3001/upload',
    cdnUrl: process.env.REACT_APP_CDN_URL || 'http://localhost:3001/static',
  },
  logging: {
    level: (process.env.REACT_APP_LOG_LEVEL as any) || 'debug',
    enableConsole: true,
  },
};

// 测试环境配置
const testConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'https://test-api.example.com/api',
  apiTimeout: 15000,
  appName: '充值管理系统 (测试版)',
  appVersion: '1.0.0-test',
  features: {
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS !== 'false',
    enableDebug: process.env.REACT_APP_ENABLE_DEBUG !== 'false',
    enableMockData: process.env.REACT_APP_ENABLE_MOCK_DATA === 'true',
  },
  services: {
    uploadUrl: process.env.REACT_APP_UPLOAD_URL || 'https://test-api.example.com/upload',
    cdnUrl: process.env.REACT_APP_CDN_URL || 'https://test-cdn.example.com',
  },
  logging: {
    level: (process.env.REACT_APP_LOG_LEVEL as any) || 'info',
    enableConsole: true,
  },
};

// 生产环境配置
const productionConfig: EnvironmentConfig = {
  apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'https://api.example.com/api',
  apiTimeout: 20000,
  appName: '充值管理系统',
  appVersion: '1.0.0',
  features: {
    enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS !== 'false',
    enableDebug: process.env.REACT_APP_ENABLE_DEBUG === 'true',
    enableMockData: process.env.REACT_APP_ENABLE_MOCK_DATA === 'true',
  },
  services: {
    uploadUrl: process.env.REACT_APP_UPLOAD_URL || 'https://api.example.com/upload',
    cdnUrl: process.env.REACT_APP_CDN_URL || 'https://cdn.example.com',
  },
  logging: {
    level: (process.env.REACT_APP_LOG_LEVEL as any) || 'warn',
    enableConsole: process.env.REACT_APP_ENABLE_CONSOLE === 'true',
  },
};

// 配置映射
const configMap: Record<Environment, EnvironmentConfig> = {
  development: developmentConfig,
  test: testConfig,
  production: productionConfig,
};

// 获取当前环境配置
export const getConfig = (): EnvironmentConfig => {
  const env = getCurrentEnvironment();
  return configMap[env];
};

// 导出当前配置
export const config = getConfig();

// 导出环境信息
export const isDevelopment = getCurrentEnvironment() === 'development';
export const isTest = getCurrentEnvironment() === 'test';
export const isProduction = getCurrentEnvironment() === 'production';

// 导出环境名称
export const environment = getCurrentEnvironment();

// 工具函数
export const getApiUrl = (endpoint: string): string => {
  return `${config.apiBaseUrl}${endpoint}`;
};

export const getUploadUrl = (): string => {
  return config.services.uploadUrl;
};

export const getCdnUrl = (path: string): string => {
  return `${config.services.cdnUrl}${path}`;
};

// 日志工具
export const logger = {
  debug: (message: string, ...args: any[]) => {
    if (config.logging.enableConsole && config.logging.level === 'debug') {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  },
  info: (message: string, ...args: any[]) => {
    if (config.logging.enableConsole && ['debug', 'info'].includes(config.logging.level)) {
      console.info(`[INFO] ${message}`, ...args);
    }
  },
  warn: (message: string, ...args: any[]) => {
    if (config.logging.enableConsole && ['debug', 'info', 'warn'].includes(config.logging.level)) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },
  error: (message: string, ...args: any[]) => {
    if (config.logging.enableConsole) {
      console.error(`[ERROR] ${message}`, ...args);
    }
  },
}; 